import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import {
  fetchUsers,
  createUser,
  updateUser,
  deleteUser,
  toggleUserStatus,
  changeUserPassword,
  fetchUserDetails,
  setFilters,
  clearError,
  resetState
} from '../store/slices/userManagementSlice';
import { UserAccount, CreateUserRequest, UpdateUserRequest, ChangePasswordRequest } from '../types/user';
import UserTable from '../components/users/UserTable';
import UserForm from '../components/users/UserForm';
import ChangePasswordModal from '../components/users/ChangePasswordModal';
import UserDetailsModal from '../components/users/UserDetailsModal';
import { 
  Search, 
  UserPlus, 
  Filter, 
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  AlertTriangle
} from 'lucide-react';

const UserManagement: React.FC = () => {
  const dispatch = useAppDispatch();
  const { 
    users, 
    loading, 
    error, 
    pagination, 
    filters,
    userDetails 
  } = useAppSelector((state) => state.userManagement);

  const [showUserForm, setShowUserForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserAccount | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState<'' | 'manager' | 'executive1' | 'executive2' | 'worker'>('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState<UserAccount | null>(null);
  const [showUserDetailsModal, setShowUserDetailsModal] = useState<UserAccount | null>(null);

  useEffect(() => {
    dispatch(fetchUsers(filters));
    
    return () => {
      dispatch(resetState());
    };
  }, [dispatch, filters]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  const handleSearch = () => {
    const newFilters = {
      ...filters,
      search: searchTerm || undefined,
      role: selectedRole || undefined,
      is_active: selectedStatus === '' ? undefined : selectedStatus === 'active',
      page: 1
    };
    dispatch(setFilters(newFilters));
  };

  const handleReset = () => {
    setSearchTerm('');
    setSelectedRole('');
    setSelectedStatus('');
    dispatch(setFilters({ page: 1, limit: 10 }));
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    setShowUserForm(true);
  };

  const handleEditUser = (user: UserAccount) => {
    setSelectedUser(user);
    setShowUserForm(true);
  };

  const handleFormSubmit = async (data: CreateUserRequest | UpdateUserRequest) => {
    try {
      if ('id' in data) {
        await dispatch(updateUser(data)).unwrap();
      } else {
        await dispatch(createUser(data)).unwrap();
      }
      setShowUserForm(false);
      dispatch(fetchUsers(filters));
    } catch (error) {
      // Error handling is done in the slice
    }
  };
  const handleDeleteUser = async (userId: number) => {
    try {
      await dispatch(deleteUser(userId)).unwrap();
      setShowDeleteConfirm(null);
      
      // Show success message
      setTimeout(() => {
        // You could add a toast notification here
        console.log('User deleted successfully');
      }, 100);
      
      // Refresh the user list
      dispatch(fetchUsers(filters));
    } catch (error) {
      console.error('Delete failed:', error);
      // Keep the delete confirmation open and show error
      // The error will be displayed via the error state
    }
  };

  const handleToggleUserStatus = async (userId: number) => {
    try {
      await dispatch(toggleUserStatus(userId)).unwrap();
      dispatch(fetchUsers(filters)); // Refresh the user list
    } catch (error) {
      console.error('Toggle status failed:', error);
    }
  };

  const handleChangePassword = (user: UserAccount) => {
    setShowChangePasswordModal(user);
  };

  const handlePasswordSubmit = async (passwordData: ChangePasswordRequest) => {
    if (!showChangePasswordModal) return;
    
    try {
      await dispatch(changeUserPassword({ 
        userId: showChangePasswordModal.id, 
        passwordData 
      })).unwrap();
      setShowChangePasswordModal(null);
    } catch (error) {
      console.error('Password change failed:', error);
    }
  };

  const handleViewDetails = async (user: UserAccount) => {
    try {
      await dispatch(fetchUserDetails(user.id)).unwrap();
      setShowUserDetailsModal(user);
    } catch (error) {
      console.error('Failed to fetch user details:', error);
    }
  };

  const handlePageChange = (page: number) => {
    dispatch(setFilters({ ...filters, page }));
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
        <p className="mt-2 text-gray-600">Manage user accounts and permissions</p>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search
            </label>
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, email, or username"
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Role
            </label>            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value as '' | 'manager' | 'executive1' | 'executive2' | 'worker')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Roles</option>
              <option value="manager">Manager</option>
              <option value="executive1">Executive 1</option>
              <option value="executive2">Executive 2</option>
              <option value="worker">Worker</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="flex items-end space-x-2">
            <button
              onClick={handleSearch}
              className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Filter className="h-4 w-4 mr-2" />
              Filter
            </button>
            <button
              onClick={handleReset}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Action Bar */}
      <div className="flex justify-between items-center mb-6">
        <div className="text-sm text-gray-700">
          Showing {users.length} of {pagination.total_records} users
        </div>
        <button
          onClick={handleCreateUser}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <UserPlus className="h-4 w-4 mr-2" />
          Add User
        </button>
      </div>      {/* User Table */}
      <UserTable
        users={users}
        onEdit={handleEditUser}
        onDelete={(userId) => setShowDeleteConfirm(userId)}
        onToggleStatus={handleToggleUserStatus}
        onChangePassword={handleChangePassword}
        onViewDetails={handleViewDetails}
        loading={loading}
      />

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-6 rounded-lg shadow">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(pagination.current_page - 1)}
              disabled={pagination.current_page === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(pagination.current_page + 1)}
              disabled={pagination.current_page === pagination.total_pages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing{' '}
                <span className="font-medium">
                  {(pagination.current_page - 1) * pagination.per_page + 1}
                </span>{' '}
                to{' '}
                <span className="font-medium">
                  {Math.min(pagination.current_page * pagination.per_page, pagination.total_records)}
                </span>{' '}
                of{' '}
                <span className="font-medium">{pagination.total_records}</span>{' '}
                results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => handlePageChange(pagination.current_page - 1)}
                  disabled={pagination.current_page === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                {[...Array(pagination.total_pages)].map((_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        page === pagination.current_page
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  onClick={() => handlePageChange(pagination.current_page + 1)}
                  disabled={pagination.current_page === pagination.total_pages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* User Form Modal */}
      <UserForm
        user={selectedUser}
        isOpen={showUserForm}
        onClose={() => setShowUserForm(false)}
        onSubmit={handleFormSubmit}
        loading={loading}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">Delete User</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Are you sure you want to delete this user? This action cannot be undone.
                </p>
                {error && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                )}
              </div>
              <div className="items-center px-4 py-3">
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      setShowDeleteConfirm(null);
                      dispatch(clearError()); // Clear any previous errors
                    }}
                    disabled={loading}
                    className="px-4 py-2 bg-gray-500 text-white text-base font-medium rounded-md shadow-sm hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleDeleteUser(showDeleteConfirm)}
                    disabled={loading}
                    className="inline-flex items-center px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Deleting...
                      </>
                    ) : (
                      'Delete'
                    )}
                  </button>
                </div>
              </div>
            </div>          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showChangePasswordModal && (
        <ChangePasswordModal
          user={showChangePasswordModal}
          isOpen={true}
          onClose={() => setShowChangePasswordModal(null)}
          onSubmit={handlePasswordSubmit}
          loading={loading}
        />
      )}      {/* User Details Modal */}
      {showUserDetailsModal && (
        <UserDetailsModal
          userDetails={userDetails}
          isOpen={true}
          onClose={() => setShowUserDetailsModal(null)}
          loading={loading}
        />
      )}
    </div>
  );
};

export default UserManagement;
